/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import PropTypes from 'prop-types';
import { compose, getContext, withState } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import connectToRouter from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import { Button, Grid, Hidden, LoadingItem, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import { track } from '@coursera/event-pulse/sdk';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
// eslint-disable-next-line
import withApiHandler from 'bundles/coursera-ui/components/hocs/withApiHandler';
import { createContainer } from 'bundles/naptimejs';
import ProfilesV1 from 'bundles/naptimejs/resources/profiles.v1';
import IntroPageContainer from 'bundles/onboarding-2022/components/survey/IntroPage';
import LoadingPage from 'bundles/onboarding-2022/components/survey/LoadingPage';
import OnboardingPageContent from 'bundles/onboarding-2022/components/survey/OnboardingPageContent';
import { OnboardingHeader } from 'bundles/onboarding-2022/components/survey/OnboardingPageHeader';
import { SCREEN_WIDTH_FOR_MOBILE } from 'bundles/onboarding-2022/data/constants';
import { getCurrentSection } from 'bundles/onboarding-2022/data/onboardingPageUtils';
import type {
  NavigationButtonInputProps,
  PropsWithOnboardingData,
  SkipButtonInputProps,
} from 'bundles/onboarding-2022/types/OnboardingPageControls';
import type {
  NonTerminalSurveyData,
  QuestionAnswer,
  SurveyData,
  SurveyRequestAnswers,
} from 'bundles/onboarding-2022/types/Survey';
import { createSurvey, updateSurvey } from 'bundles/onboarding-2022/utils/apiUtils';
import withUserAgent from 'bundles/page/common/withUserAgent';

import _t from 'i18n!nls/onboarding-2022';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

type EventData = {
  namespace: {
    app: string;
    page: string;
  };
};

type State = {
  surveyDataBySection?: Record<string, SurveyData>;
  answers: SurveyRequestAnswers;
  isLoading: boolean;
};

type Context = {
  _eventData: EventData;
};

const styles = {
  onboardingPage: () => css`
    position: relative;

    .onboarding-next-button {
      display: inline-block;
    }

    .onboarding-back-button {
      display: inline-block;
      padding-right: 16px;
    }

    button {
      border-radius: 5px;
    }

    svg {
      margin-left: 5px;
    }
  `,
  onboardingContents: css`
    ${breakpoints.up('md')} {
      margin: auto;
      width: 776px;
    }
    ${breakpoints.between('sm', 'md')} {
      width: 60%;
    }
    ${breakpoints.down('sm')} {
      margin: auto;
      width: 75%;
    }
    ${breakpoints.down('xs')} {
      width: 90%;
    }
  `,
  onboardingContentsContainer: css`
    margin: auto;
    ${breakpoints.up('sm')} {
      margin: auto;
      width: 552px;
    }
    ${breakpoints.down('xs')} {
      max-width: 334px;
    }
    ${breakpoints.down(375)} {
      max-width: 200px;
    }
    ${breakpoints.between('xs', 'sm')} {
      width: 452px;
      margin: auto;
    }
  `,
  nextButton: css`
    display: flex;
    flex-direction: row;
    justify-content: right;
    align-items: center;
  `,
  introNextButton: css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  `,

  buttonContainer: css`
    padding: 24px;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
  `,
};

export const OnboardingPageNextButton = ({ pageIndex, onClick, disabled }: NavigationButtonInputProps) => {
  return (
    <div>
      <Hidden mdUp>
        <TrackedButton
          {...Button.defaultProps}
          trackingName="onboarding_next_button"
          // eslint-disable-next-line camelcase
          trackingData={{ current_page_index: pageIndex }}
          variant="primary"
          onClick={onClick}
          data-e2e="onboarding-next-button"
          fullWidth
          disabled={disabled}
        >
          {_t('Next')}
          <ArrowNextIcon size="small" />
        </TrackedButton>
      </Hidden>
      <Hidden smDown>
        <TrackedButton
          {...Button.defaultProps}
          trackingName="onboarding_next_button"
          // eslint-disable-next-line camelcase
          trackingData={{ current_page_index: pageIndex }}
          variant="primary"
          onClick={onClick}
          data-e2e="onboarding-next-button"
          disabled={disabled}
        >
          {_t('Next')}
          <ArrowNextIcon size="small" />
        </TrackedButton>
      </Hidden>
    </div>
  );
};

export const OnboardingPageSkipButton = ({ pageIndex, onClick, disabled }: SkipButtonInputProps) => {
  return (
    <Grid>
      <Hidden mdUp>
        <TrackedButton
          {...Button.defaultProps}
          trackingName="onboarding_skip_button"
          // eslint-disable-next-line camelcase
          trackingData={{ current_page_index: pageIndex }}
          variant="ghost"
          data-e2e="onboarding-skip-button"
          onClick={onClick}
          disabled={disabled}
          fullWidth
        >
          {_t('Skip')}
        </TrackedButton>
      </Hidden>
      <Hidden smDown>
        <TrackedButton
          {...Button.defaultProps}
          trackingName="onboarding_skip_button"
          // eslint-disable-next-line camelcase
          trackingData={{ current_page_index: pageIndex }}
          variant="ghost"
          data-e2e="onboarding-skip-button"
          onClick={onClick}
          disabled={disabled}
        >
          {_t('Skip')}
        </TrackedButton>
      </Hidden>
    </Grid>
  );
};

export class OnboardingPageControls extends React.Component<PropsWithOnboardingData, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof OnboardingPageControls.contextTypes>;

  constructor(props: PropsWithOnboardingData, context: Context) {
    super(props, context);

    this.state = {
      isLoading: false,
      answers: {},
    };
  }

  componentDidMount() {
    this.props.setRouter(this.context.router);
  }

  isPageLoadingPage = () => {
    return this.getCurrentSectionSurveyData()?.isTerminal ?? false;
  };

  componentDidUpdate() {
    if (this.isPageLoadingPage()) {
      this.onLoadingHasFinished();
    }
  }

  getCurrentSectionSurveyData = (): SurveyData | undefined => {
    const { surveyDataBySection } = this.state;
    const { sectionFlow } = this.props;

    const currentSection = getCurrentSection(sectionFlow);
    return currentSection ? surveyDataBySection?.[currentSection] : undefined;
  };

  isNonTerminalSurvey = (surveyData?: SurveyData): surveyData is NonTerminalSurveyData => {
    return Boolean(surveyData && !surveyData.isTerminal);
  };

  setSurveyData = (newSurveyData?: SurveyData | undefined) => {
    if (newSurveyData) {
      this.setState((prev) => ({
        surveyDataBySection: {
          ...prev.surveyDataBySection,
          [newSurveyData.sectionId]: newSurveyData,
        },
      }));
    }
    return newSurveyData;
  };

  updateLocation = (sectionFlow: string[]) => {
    const { router } = this.context;
    const { query } = router.location;
    query.pageNumber = sectionFlow.length;
    router.replace({ ...router.location, query });
  };

  requestSurvey = () => {
    const sectionSurveyData = this.getCurrentSectionSurveyData();
    const { sectionFlow } = this.props;

    this.setState({ isLoading: true });

    let request: Promise<SurveyData | undefined> = Promise.resolve(undefined);

    const surveyShouldBeCreated = sectionFlow.length === 0;
    const surveyShouldBeUpdated = this.isNonTerminalSurvey(sectionSurveyData) && sectionFlow.length > 0;

    if (surveyShouldBeCreated) {
      request = createSurvey();
    } else if (surveyShouldBeUpdated) {
      const { responseId, sectionId, questions } = sectionSurveyData;
      const answers = this.state.answers;
      request = updateSurvey({ responseId, sectionId, questions, answers });
    }
    return request.then(this.setSurveyData).finally(() => this.setState({ isLoading: false }));
  };

  goToNextPage = () => {
    const promisesToResolve = [];

    const hasNotCompletedTheSurveyFlow = !this.isPageLoadingPage();

    if (hasNotCompletedTheSurveyFlow) {
      const { sectionFlow, setSectionFlow } = this.props;
      promisesToResolve.push(
        this.requestSurvey().then((newSurveyData) => {
          if (newSurveyData) {
            const newSectionFlow = [...sectionFlow, newSurveyData.sectionId];

            this.updateLocation(newSectionFlow);
            setSectionFlow(newSectionFlow);
            if (newSurveyData?.isTerminal) {
              track('click_button', {
                form: {
                  name: 'onboarding_consumer_multi_page_2022',
                  currentStep: newSectionFlow.length,
                  totalSteps: newSectionFlow.length,
                },
                button: { name: 'onboarding_submit' },
              });
            }
          }

          return newSurveyData;
        })
      );
    }
    Promise.all(promisesToResolve).then(() => {
      document.getElementById('onboardingFocusGrid')?.focus();
    });
  };

  goToPreviousPage = () => {
    const { sectionFlow, setSectionFlow } = this.props;
    const newSectionFlow = sectionFlow.slice(0, sectionFlow.length - 1);
    setSectionFlow(newSectionFlow);
    this.updateLocation(newSectionFlow);
  };

  isSectionCompleted = (): boolean => {
    const { answers } = this.state;
    const surveyData = this.getCurrentSectionSurveyData();
    let completed = false;

    if (this.isNonTerminalSurvey(surveyData)) {
      const { questions, sectionId } = surveyData;

      if (Object.keys(answers).length !== 0 && sectionId in answers) {
        completed = true;
        questions.forEach((question) => {
          if (!answers[sectionId][question.id]) completed = false;
        });
      }
    }
    return completed;
  };

  onLoadingHasFinished = () => {
    return window.location.replace('/');
  };

  renderSecondaryPageContent = () => {
    if (this.isPageLoadingPage()) {
      return <LoadingPage onFinishedLoading={this.onLoadingHasFinished} />;
    } else {
      return null;
    }
  };

  setAnswers = (answers: QuestionAnswer) => {
    const surveyData = this.getCurrentSectionSurveyData();
    const stateAnswers = this.state.answers;
    const answerQuestion = Object.keys(answers)[0];

    if (this.isNonTerminalSurvey(surveyData)) {
      const { sectionId } = surveyData;
      if (stateAnswers[sectionId]) {
        const sectionAnswers = stateAnswers[sectionId];
        sectionAnswers[answerQuestion] = answers[answerQuestion];
        this.setState((prev) => ({ answers: { ...prev.answers, [sectionId]: sectionAnswers } }));
      } else {
        this.setState((prev) => ({ answers: { ...prev.answers, [sectionId]: answers } }));
      }
    }
  };

  render = () => {
    const { userAgent } = this.props;

    const { answers, isLoading } = this.state;
    const { sectionFlow } = this.props;
    const currentSectionSurveyData = this.getCurrentSectionSurveyData();
    const sectionCompleted = this.isSectionCompleted();
    const isLoadingPage = this.isPageLoadingPage();
    const trackingPageIndex = sectionFlow.length;

    return (
      <div role="main" css={styles.onboardingPage}>
        <Grid container direction="column">
          <Grid id="onboardingFocusGrid" tabIndex={-1} />
          <Media query={{ maxWidth: SCREEN_WIDTH_FOR_MOBILE - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
            {(isMobile) => (
              <OnboardingHeader
                isMobile={isMobile}
                sectionFlow={sectionFlow}
                switchPage={this.goToPreviousPage}
                isLoadingPage={isLoadingPage}
              />
            )}
          </Media>

          {
            // Next button for intro page web and mobile-web
            sectionFlow.length === 0 && !isLoadingPage && (
              <Media query={{ maxWidth: SCREEN_WIDTH_FOR_MOBILE - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
                {(isMobile) => (
                  <Grid>
                    <IntroPageContainer isMobile={isMobile} />
                    {!isMobile ? (
                      <Grid>
                        <Grid css={styles.introNextButton}>
                          <OnboardingPageNextButton
                            pageIndex={trackingPageIndex}
                            onClick={this.goToNextPage}
                            disabled={false}
                          />
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid>
                        <Grid>
                          <OnboardingPageNextButton
                            pageIndex={trackingPageIndex}
                            onClick={this.goToNextPage}
                            disabled={false}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                )}
              </Media>
            )
          }
          <Grid item css={styles.onboardingContents}>
            {this.renderSecondaryPageContent()}
            {sectionFlow.length > 0 && !isLoadingPage && (
              <Grid
                css={css`
                  padding-bottom: 40px;
                  margin: 0 16px;
                `}
              >
                <Grid css={styles.onboardingContentsContainer}>
                  <Grid>
                    {currentSectionSurveyData && (
                      <OnboardingPageContent
                        currentPageIndex={trackingPageIndex}
                        currentSectionSurveyData={currentSectionSurveyData}
                        answers={answers}
                        onChange={this.setAnswers}
                      />
                    )}

                    <Media
                      query={{ maxWidth: SCREEN_WIDTH_FOR_MOBILE - 1 }}
                      defaultMatches={userAgent?.isMobileBrowser}
                    >
                      {
                        // buttons for rest of survey mobile and mobile-web
                        (isMobile) =>
                          !isMobile ? (
                            <Grid css={styles.nextButton}>
                              {isLoading && <LoadingItem label="" />}
                              <OnboardingPageSkipButton
                                pageIndex={trackingPageIndex}
                                onClick={this.goToNextPage}
                                disabled={isLoading}
                              />
                              <OnboardingPageNextButton
                                pageIndex={trackingPageIndex}
                                onClick={this.goToNextPage}
                                disabled={!sectionCompleted || isLoading}
                              />
                            </Grid>
                          ) : (
                            <Grid>
                              <OnboardingPageNextButton
                                pageIndex={trackingPageIndex}
                                onClick={this.goToNextPage}
                                disabled={!sectionCompleted || isLoading}
                              />
                              <OnboardingPageSkipButton
                                pageIndex={trackingPageIndex}
                                onClick={this.goToNextPage}
                                disabled={isLoading}
                              />
                            </Grid>
                          )
                      }
                    </Media>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </div>
    );
  };
}

export default compose<PropsWithOnboardingData, {}>(
  connectToRouter((router) => ({
    query: router.location.query.query || '',
  })),
  withUserAgent(),
  createContainer(() => {
    const userId = user.get().id;

    return {
      profile: ProfilesV1.get(userId, {}),
    };
  }),
  withState('sectionFlow', 'setSectionFlow', []),
  withState('router', 'setRouter', null),
  withApiHandler({}),
  getContext({ _eventData: PropTypes.object.isRequired })
)(OnboardingPageControls);
